import {Injectable, NgZone} from "@angular/core";
import {default as OneSignalPlugin, UserChangedState} from "onesignal-cordova-plugin";
import {Observable} from "rxjs";
import {PushSubscription} from "./push-subscription";

@Injectable()
export class User {
  constructor(
    private readonly ngZone: NgZone,
    readonly pushSubscription: PushSubscription,
  ) {
  }

  /**
   * Listen for User state changes.
   * Important: When using the observer to retrieve the onesignalId, check the externalId as well to confirm the values are associated with the expected user.
   */
  onChange(): Observable<UserChangedState> {
    return this.ngZone.runOutsideAngular(() => new Observable<UserChangedState>((observer) => {
      const listener = (event: UserChangedState) => {
        this.ngZone.run(() => {
          observer.next(event);
        });
      };

      OneSignalPlugin.User.addEventListener('change', listener);

      return () => {
        OneSignalPlugin.User.removeEventListener('change', listener);
      };
    }));
  }

  /**
   * Explicitly set a 2-character language code for the user.
   */
  setLanguage(language: string): void {
    OneSignalPlugin.User.setLanguage(language);
  };

  /**
   * Set an alias for the current user. If this alias label already exists on this user, it will be overwritten with the new alias id.
   */
  addAlias(label: string, id: string): void {
    OneSignalPlugin.User.addAlias(label, id);
  };

  /**
   * Set aliases for the current user. If any alias already exists, it will be overwritten to the new values.
   */
  addAliases(aliases: Record<string, string>): void {
    OneSignalPlugin.User.addAliases(aliases);
  };

  /**
   * Remove an alias from the current user.
   */
  removeAlias(label: string): void {
    OneSignalPlugin.User.removeAlias(label);
  };

  /**
   * Remove aliases from the current user.
   */
  removeAliases(labels: string[]): void {
    OneSignalPlugin.User.removeAliases(labels);
  };

  /**
   * Add a new email subscription to the current user.
   */
  addEmail(email: string): void {
    OneSignalPlugin.User.addEmail(email);
  };

  /**
   * Remove an email subscription from the current user. Returns false if the specified email does not exist on the user within the SDK, and no request will be made.
   */
  removeEmail(email: string): void {
    OneSignalPlugin.User.removeEmail(email);
  };

  /**
   * Add a new SMS subscription to the current user.
   */
  addSms(smsNumber: string): void {
    OneSignalPlugin.User.addSms(smsNumber);
  };

  /**
   * Remove an SMS subscription from the current user. Returns false if the specified SMS number does not exist on the user within the SDK, and no request will be made.
   */
  removeSms(smsNumber: string): void {
    OneSignalPlugin.User.removeSms(smsNumber);
  };

  /**
   * Add a tag for the current user. Tags are key:value string pairs used as building blocks for targeting specific users and/or personalizing messages. If the tag key already exists, it will be replaced with the value provided here.
   */
  addTag(key: string, value: string): void {
    OneSignalPlugin.User.addTag(key, value);
  };

  /**
   * Add multiple tags for the current user. Tags are key:value string pairs used as building blocks for targeting specific users and/or personalizing messages. If the tag key already exists, it will be replaced with the value provided here.
   */
  addTags(tags: Record<string, string>): void {
    OneSignalPlugin.User.addTags(tags);
  };

  /**
   * Remove the data tag with the provided key from the current user.
   */
  removeTag(key: string): void {
    OneSignalPlugin.User.removeTag(key);
  };

  /**
   * Remove multiple tags with the provided keys from the current user.
   */
  removeTags(keys: string[]): void {
    OneSignalPlugin.User.removeTags(keys);
  };

  /**
   * Returns the local tags for the current user.
   */
  getTags(): Promise<Record<string, string>> {
    return this.ngZone.runOutsideAngular(() => new Promise<Record<string, string>>((resolve, reject) => {
      OneSignalPlugin.User.getTags()
        .then(
          (result) => {
            this.ngZone.run(() => {
              resolve(result);
            });
          },
          (reason) => {
            this.ngZone.run(() => {
              reject(reason);
            });
          },
        );
    }));
  };

  /**
   * Get the nullable OneSignal Id associated with the current user.
   */
  getOnesignalId(): Promise<string | null> {
    return this.ngZone.runOutsideAngular(() => new Promise<string | null>((resolve, reject) => {
      OneSignalPlugin.User.getOnesignalId()
        .then(
          (result) => {
            this.ngZone.run(() => {
              resolve(result);
            });
          },
          (reason) => {
            this.ngZone.run(() => {
              reject(reason);
            });
          },
        );
    }));
  }

  /**
   * Get the nullable External Id associated with the current user.
   */
  getExternalId(): Promise<string | null> {
    return this.ngZone.runOutsideAngular(() => new Promise<string | null>((resolve, reject) => {
      OneSignalPlugin.User.getExternalId()
        .then(
          (result) => {
            this.ngZone.run(() => {
              resolve(result);
            });
          },
          (reason) => {
            this.ngZone.run(() => {
              reject(reason);
            });
          },
        );
    }));
  }
}
